import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { useScrollReveal } from "../components/useScrollReveal";
import { useCountUp } from "../components/useCountUp";
import { useSEO } from "../components/useSEO";
import "./WhoWeHelp.css";

export default function WhoWeHelp() {
  useScrollReveal();
  useCountUp();
  useSEO(
    "Who We Help | Modus PDM for Workers' Comp, Payers & Employers",
    "Provider data management (PDM) for workers' comp carriers, TPAs, health plans, and self-funded employers. Verified provider data, scored on outcomes."
  );

  return (
    <>
      <SiteHeader active="who-we-help" />


<section className="wh-hero" style={{paddingBottom: "60px"}}>
  <div className="wrap plat-hero-grid">
    <div className="plat-hero-copy">
      <p className="kicker">Who We Help</p>
      <h1>Built for the teams making the network call.</h1>
      <p>Four audiences, one problem: knowing who's real, who's good, and who's actually taking patients, right now.</p>
      <div className="modjump">
        <a href="#workers-comp">WC &amp; Group Health Payers</a>
        <a href="#credentialing">Credentialing &amp; Care Navigation</a>
        <a href="#ime">IME Companies</a>
        <a href="#tpas">TPAs</a>
      </div>
    </div>
    <div className="plat-hero-visual">
      <div className="phv-frame">
        <div className="phv-search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round"><circle cx="11" cy="11" r="7"></circle><line x1="21" y1="21" x2="16.6" y2="16.6"></line></svg>
          <span>Find your team's fit...</span>
          <span className="phv-live"><span className="dot"></span>Live</span>
        </div>
        <div className="phv-stack">
          <div className="phv-layer" style={{"--i": "0"}}><span className="phv-ico"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg></span><div><div className="phv-t">WC &amp; Group Health Payers</div><div className="phv-s">Audit-ready panels, real-time proof</div></div><span className="phv-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span></div>
          <div className="phv-layer" style={{"--i": "1"}}><span className="phv-ico"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/></svg></span><div><div className="phv-t">Credentialing &amp; Care Navigation</div><div className="phv-s">Quality and cost in every referral</div></div><span className="phv-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span></div>
          <div className="phv-layer" style={{"--i": "2"}}><span className="phv-ico"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg></span><div><div className="phv-t">IME Companies</div><div className="phv-s">The selection engine for examiners</div></div><span className="phv-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span></div>
          <div className="phv-layer" style={{"--i": "3"}}><span className="phv-ico"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/><line x1="9" y1="9" x2="9" y2="9.01"/><line x1="9" y1="13" x2="9" y2="13.01"/><line x1="9" y1="17" x2="9" y2="17.01"/></svg></span><div><div className="phv-t">TPAs</div><div className="phv-s">Compliant directories, live routing</div></div><span className="phv-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span></div>
        </div>
      </div>
    </div>
  </div>
</section>

<section id="workers-comp">
  <div className="wrap">
    <div className="aud-block reveal">
      <div className="aud-copy">
        <span className="aud-tag">WC &amp; Group Health Payers</span>
        <h2 className="aud-title">Audit-ready panels, for workers' comp and group health alike.</h2>
        <p>Modus was built inside workers' comp first, and the same model travels cleanly to self-insured employers and group health carriers. Doctors' practice patterns stay consistent across payers, so Medicare-derived scoring transfers directly, and NPI-level data avoids the tax-ID confusion that plagues WC directories.</p>
        <ul>
          <li>Jurisdiction-compliant, audit-ready provider panels</li>
          <li>Real-time proof of who's actually accepting patients, not a stale annual list</li>
          <li>Modus direct-contracting model available</li>
          <li>Workers 60+ are WC's fastest-growing group, and look increasingly like Medicare's population</li>
        </ul>
      </div>
      <div className="aud-card">
        <div className="aud-stat"><span className="lbl">Historical patient records analyzed</span><span className="val"><span data-count="183" data-suffix="M+">0M+</span></span></div>
        <div className="aud-stat"><span className="lbl">Guaranteed ROI on direct-contracting engagements</span><span className="val"><span data-count="2" data-suffix="x">0x</span></span></div>
        <div className="aud-stat"><span className="lbl">Fastest-growing WC demographic</span><span className="val">Age 60+</span></div>
      </div>
    </div>
  </div>
</section>

<section id="credentialing" style={{background: "var(--surface)"}}>
  <div className="wrap">
    <div className="aud-block rev reveal">
      <div className="aud-copy">
        <span className="aud-tag">Credentialing &amp; Care Navigation</span>
        <h2 className="aud-title">You verify who they are. We tell you if they're good.</h2>
        <p>Credentialing already confirms a provider is who they say they are. Care navigation and concierge teams need to go one step further and know if that provider is actually good. Modus layers quality and cost scoring on top of either workflow: a resellable, premium tier for credentialing firms, and a decision engine for navigators making the referral call.</p>
        <ul>
          <li>White-label-ready scoring layer on top of your existing workflow</li>
          <li>Built into every referral for care navigation and concierge teams</li>
          <li>Open API for direct integration into your platform</li>
          <li>Independent, no hospital or insurer affiliation to disclose</li>
        </ul>
      </div>
      <div className="aud-card">
        <div className="aud-stat"><span className="lbl">Validation Institute guarantee</span><span className="val"><span data-count="100" data-prefix="$" data-suffix="K">$0K</span></span></div>
        <div className="aud-stat"><span className="lbl">Integration options</span><span className="val">API · Teams · MCP</span></div>
        <div className="aud-stat"><span className="lbl">Methodology</span><span className="val">Published, transparent</span></div>
      </div>
    </div>
  </div>
</section>

<section id="ime">
  <div className="wrap">
    <div className="aud-block reveal">
      <div className="aud-copy">
        <span className="aud-tag">IME Companies</span>
        <h2 className="aud-title">Picking the right examiner is the whole business.</h2>
        <p>Independent medical exam networks live or die on examiner selection. Modus Score is built to be that selection engine, an objective, peer-adjusted read on practice pattern, not a reputation guess.</p>
        <ul>
          <li>Score examiners the same way we score treating physicians</li>
          <li>Continuous red-flag monitoring on your examiner panel</li>
          <li>API or Teams-native access for scheduling coordinators</li>
        </ul>
      </div>
      <div className="aud-card">
        <div className="aud-stat"><span className="lbl">Specialties covered today</span><span className="val"><span data-count="20" data-suffix="+">0+</span></span></div>
        <div className="aud-stat"><span className="lbl">Data refresh cycle</span><span className="val">Yearly</span></div>
        <div className="aud-stat"><span className="lbl">Peer comparison basis</span><span className="val">Specialty + geography</span></div>
      </div>
    </div>
  </div>
</section>

<section id="tpas" style={{background: "var(--surface)"}}>
  <div className="wrap">
    <div className="aud-block rev reveal">
      <div className="aud-copy">
        <span className="aud-tag">TPAs</span>
        <h2 className="aud-title">Compliant directories. Claims routed to providers who are actually there.</h2>
        <p>Directory accuracy is compliance-mandated: CMS directory fines and No Surprises Act exposure make this a budget line TPAs already fund. Modus enters through that same mandatory spend, then adds the performance layer legacy directory vendors can't, so claims get routed to providers who are actually in-network and open.</p>
        <ul>
          <li>Continuous, AI-verified directory accuracy, not an annual refresh</li>
          <li>Claims routed to providers who are actually accepting patients</li>
          <li>Provider Performance scoring layered on top of compliance data</li>
          <li>Live inside Microsoft Teams for network and claims teams</li>
        </ul>
      </div>
      <div className="aud-card">
        <div className="aud-stat"><span className="lbl">Provider data that changes yearly</span><span className="val"><span data-count="20" data-prefix="~" data-suffix="%">~0%</span></span></div>
        <div className="aud-stat"><span className="lbl">CMS fine per beneficiary, directory error</span><span className="val"><span data-count="25" data-prefix="$" data-suffix="K">$0K</span></span></div>
        <div className="aud-stat"><span className="lbl">Re-verification cycle (REAL Act)</span><span className="val"><span data-count="90" data-suffix=" days">0 days</span></span></div>
      </div>
    </div>
  </div>
</section>

<section id="evidence" style={{background: "var(--surface)"}}>
  <div className="wrap">
    <span className="aud-tag">Carrier network analysis</span>
    <h2 className="aud-title" style={{maxWidth: "680px"}}>Low-rated doctors cost <span style={{color: "var(--teal-ink)"}}>90% more per claim</span></h2>
    <p className="ev-lead">A midsize workers' compensation carrier asked us to rate the physicians already in its network. Over three years we scored 3,209 of them against 7,909 claims and $54.1M in allowed spend.</p>

    <div className="ev-chart-card reveal">
      <div className="ev-chart">
        <div className="ev-bar-col">
          <div className="ev-bar-val">$3,712</div>
          <div className="ev-bar high" style={{height: "46.4%"}}></div>
          <div className="ev-bar-cat">High-rated</div>
        </div>
        <div className="ev-bar-col">
          <div className="ev-bar-val low">$7,077</div>
          <div className="ev-bar low" style={{height: "88.5%"}}></div>
          <div className="ev-bar-cat">Low-rated</div>
        </div>
      </div>
      <p className="ev-caption">Allowed amount per claim</p>
    </div>

    <div className="ev-stat-strip reveal">
      <div className="ev-stat"><span className="ev-stat-n">3,209</span><span className="ev-stat-l">Physicians scored</span></div>
      <div className="ev-stat"><span className="ev-stat-n">7,909</span><span className="ev-stat-l">Claims</span></div>
      <div className="ev-stat"><span className="ev-stat-n">$54.1M</span><span className="ev-stat-l">Allowed spend</span></div>
      <div className="ev-stat"><span className="ev-stat-n">66%</span><span className="ev-stat-l">Spend with low-rated</span></div>
    </div>

  </div>
</section>

<section style={{background: "var(--page)"}}>
  <div className="wrap">
    <div className="section-head reveal wide">
      <p className="kicker">Who we help</p>
      <h2 className="section-title nowrap-title">Built for the teams making the network call.</h2>
      <p className="section-lead" style={{maxWidth: "640px"}}>Four audiences, one problem: knowing who's real, who's good, and who's actually taking patients, right now.</p>
    </div>
    <div className="who-grid reveal-stagger-3d">
      <div className="who-card">
        <div className="who-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg></div>
        <h3>WC &amp; Group Health Payers</h3>
        <p>Built for self-insured employers and carriers: audit-ready panels with real-time proof of who's actually accepting patients.</p>
        <a className="who-link" href="#workers-comp">Learn more <span className="arrow">→</span></a>
      </div>
      <div className="who-card">
        <div className="who-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/></svg></div>
        <h3>Credentialing &amp; Care Navigation</h3>
        <p>For credentialing teams and care navigation or concierge services that need provider quality and cost baked into every referral.</p>
        <a className="who-link" href="#credentialing">Learn more <span className="arrow">→</span></a>
      </div>
      <div className="who-card">
        <div className="who-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg></div>
        <h3>IME Companies</h3>
        <p>Picking the right examiner is the whole business. Modus Score is built to be that selection engine.</p>
        <a className="who-link" href="#ime">Learn more <span className="arrow">→</span></a>
      </div>
      <div className="who-card">
        <div className="who-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/><line x1="9" y1="9" x2="9" y2="9.01"/><line x1="9" y1="13" x2="9" y2="13.01"/><line x1="9" y1="17" x2="9" y2="17.01"/></svg></div>
        <h3>TPAs</h3>
        <p>Keep client directories compliant and claims routed to providers who are actually in-network and open.</p>
        <a className="who-link" href="#tpas">Learn more <span className="arrow">→</span></a>
      </div>
    </div>
  </div>
</section>


      <SiteFooter />
    </>
  );
}